import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  TrendingUp,
  DollarSign,
  Package,
  ShoppingCart,
  RefreshCw,
  BarChart3,
  ArrowRight
} from 'lucide-react';
import { toast } from 'react-toastify';
import { getConsolidatedMetrics, compareBranches } from '../../api/analytics';
import { getBranches } from '../../api/branches';
import BlurredValue from '../../components/common/BlurredValue';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const ConsolidatedDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [branches, setBranches] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num || 0);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const endDate = new Date().toISOString();
      const startDate = new Date(
        Date.now() - parseInt(dateRange) * 24 * 60 * 60 * 1000
      ).toISOString();

      const [branchesData, metricsData] = await Promise.all([
        getBranches(),
        getConsolidatedMetrics(startDate, endDate)
      ]);

      setBranches(branchesData.branches || branchesData || []);
      setMetrics(metricsData);
    } catch (error) {
      toast.error('Failed to load dashboard data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return <LoadingSpinner className="min-h-screen" />;
  }

  const maxRevenue = Math.max(
    ...(metrics?.by_branch || []).map((b) => parseFloat(b.revenue) || 0),
    1
  );

  return (
    <div className="min-h-screen bg-secondary-offWhite p-3 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-primary-black">
                Consolidated Dashboard
              </h1>
              <p className="text-sm text-gray-500">Overview of all branches</p>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-accent-gold"
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
                <option value="365">Last year</option>
              </select>
              <button onClick={fetchData} className="p-2 border rounded-lg hover:bg-gray-50">
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Total KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-sm p-4 text-white">
            <div className="flex items-center gap-2 mb-2 opacity-80">
              <ShoppingCart className="w-5 h-5" />
              <span className="text-sm font-medium">Total Sales</span>
            </div>
            <p className="text-2xl sm:text-3xl font-bold">
              {formatNumber(metrics?.sales?.total_sales)}
            </p>
            <p className="text-xs opacity-75 mt-1">
              {metrics?.sales?.active_branches} active branches
            </p>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-sm p-4 text-white">
            <div className="flex items-center gap-2 mb-2 opacity-80">
              <DollarSign className="w-5 h-5" />
              <span className="text-sm font-medium">Total Revenue</span>
            </div>
            <p className="text-xl sm:text-2xl font-bold">
              <BlurredValue value={formatCurrency(metrics?.sales?.total_revenue)} />
            </p>
            <p className="text-xs opacity-75 mt-1">
              Profit: <BlurredValue value={formatCurrency(metrics?.sales?.total_profit)} />
            </p>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-sm p-4 text-white">
            <div className="flex items-center gap-2 mb-2 opacity-80">
              <Package className="w-5 h-5" />
              <span className="text-sm font-medium">Inventory Value</span>
            </div>
            <p className="text-xl sm:text-2xl font-bold">
              <BlurredValue
                value={formatCurrency(
                  metrics?.inventory?.reduce((sum, b) => sum + parseFloat(b.retail_value || 0), 0)
                )}
              />
            </p>
            <p className="text-xs opacity-75 mt-1">
              {formatNumber(
                metrics?.inventory?.reduce((sum, b) => sum + parseInt(b.total_units || 0), 0)
              )}{' '}
              total units
            </p>
          </div>

          <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg shadow-sm p-4 text-white">
            <div className="flex items-center gap-2 mb-2 opacity-80">
              <TrendingUp className="w-5 h-5" />
              <span className="text-sm font-medium">Avg Sale</span>
            </div>
            <p className="text-xl sm:text-2xl font-bold">
              <BlurredValue value={formatCurrency(metrics?.sales?.avg_sale_value)} />
            </p>
            <p className="text-xs opacity-75 mt-1">
              {formatNumber(metrics?.sales?.unique_customers)} customers
            </p>
          </div>
        </div>

        {/* Branch Performance Comparison */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            Branch Performance
          </h3>

          <div className="space-y-4">
            {metrics?.by_branch?.map((branch) => {
              const revenuePercent = (parseFloat(branch.revenue) / maxRevenue) * 100;
              return (
                <div
                  key={branch.branch_id}
                  className="cursor-pointer hover:bg-gray-50 p-3 rounded-lg transition-colors"
                  onClick={() => navigate(`/branches/${branch.branch_id}/dashboard`)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">{branch.branch_name}</span>
                      <span className="text-xs text-gray-500">({branch.branch_code})</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-gray-500">{branch.sale_count} sales</span>
                      <span className="font-semibold text-accent-gold">
                        <BlurredValue value={formatCurrency(branch.revenue)} />
                      </span>
                      <ArrowRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-accent-gold to-yellow-400 rounded-full transition-all"
                      style={{ width: `${revenuePercent}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Inventory by Branch */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Package className="w-5 h-5 text-purple-600" />
            Inventory by Branch
          </h3>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Branch</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Products</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Units</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold">Cost Value</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold">Retail Value</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {metrics?.inventory?.map((inv) => (
                  <tr key={inv.branch_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{inv.branch_name}</td>
                    <td className="px-4 py-3 text-center">{formatNumber(inv.product_count)}</td>
                    <td className="px-4 py-3 text-center">{formatNumber(inv.total_units)}</td>
                    <td className="px-4 py-3 text-right text-gray-500">
                      <BlurredValue value={formatCurrency(inv.cost_value)} />
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-accent-gold">
                      <BlurredValue value={formatCurrency(inv.retail_value)} />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-100 font-semibold">
                <tr>
                  <td className="px-4 py-3">Total</td>
                  <td className="px-4 py-3 text-center">
                    {formatNumber(
                      metrics?.inventory?.reduce((s, i) => s + parseInt(i.product_count || 0), 0)
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {formatNumber(
                      metrics?.inventory?.reduce((s, i) => s + parseInt(i.total_units || 0), 0)
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <BlurredValue
                      value={formatCurrency(
                        metrics?.inventory?.reduce((s, i) => s + parseFloat(i.cost_value || 0), 0)
                      )}
                    />
                  </td>
                  <td className="px-4 py-3 text-right text-accent-gold">
                    <BlurredValue
                      value={formatCurrency(
                        metrics?.inventory?.reduce((s, i) => s + parseFloat(i.retail_value || 0), 0)
                      )}
                    />
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsolidatedDashboard;
